import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import {
  AddOrderMeta,
  SocialLoginLoading,
  SocialLoginSuccess,
  SocialLogout,
} from 'src/app/public/store/+state/stores.actions';
import {
  getSelectedStore,
  getSocialAuth,
} from 'src/app/public/store/+state/stores.selectors';
import { environment } from 'src/environments/environment';
import { SocialLoginFailed } from '../auth/+state/auth.actions';
import { ClientStore } from '../stores/stores';
import { LocationService } from './location.service';
import { StoreService } from './store/store.service';
import {
  CustomerSocialLoginResponse,
  SocialAccountLoginDetails,
} from './store/types/CustomerSocialLogin';
import { combineLatest } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerAuthService {
  readonly tokenCookieName = '__auth_customer_storage__';
  readonly authDataParam = 'authData';
  selectedStore: ClientStore;
  isAdminOrderCaptureUpdate: boolean;
  private sociallyLoggedIn = false;
  private isMember = false;
  private readonly REFRESH_TOKEN_INTERVAL = 4 * 60 * 1000; // 4 minutes
  private refreshTokenInterval: any;

  constructor(
    private store: Store,
    public sanitizer: DomSanitizer,
    private locationService: LocationService,
    private storeService: StoreService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
  ) {
    this.onInit();
  }

  private onInit(): void {
    this.isAdminOrderCaptureUpdate =
      this.locationService.isAdminOrderCaptureUpdate();
    this.listenToLoginEvents();

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getSocialAuth),
    ])
      .pipe(filter(([selectedStore]) => !!selectedStore))
      .subscribe(([selectedStore, socialAuth]) => {
        this.selectedStore = selectedStore;
        if (socialAuth?.socialLoginState && this.sociallyLoggedIn) {
          const socialLoginResponse = socialAuth.socialLoginState;
          const { socialAuthStatus } = socialLoginResponse;

          if (socialAuthStatus === 'SUCCESS') {
            if (this.membershipRequired()) {
              this.isMember = false;
              this.storeService
                .customerCheckMembership(this.selectedStore.id)
                .subscribe(
                  (res) => {
                    this.isMember = res.isMember;
                    if (!res.isMember) {
                      this.onInvalidMember();
                    }
                  },
                  () => this.onInvalidMember(),
                );
            }

            const event = {
              src: 'CustomerAuthService',
              description: 'Social login successfull',
            };
            if (socialLoginResponse.userId) {
              this.store.dispatch(
                new AddOrderMeta(
                  'customerUserId',
                  socialLoginResponse.userId,
                  event,
                ),
              );
            }
          }

          if (socialAuthStatus === 'FAILED') {
            this.logout();
          }
        }
      });
  }

  private listenToLoginEvents() {
    window.addEventListener(
      'message',
      (event) => {
        const { data } = event;
        this.login(data);
      },
      false,
    );
  }

  enableCustomerAuth(): boolean {
    return (
      (this.selectedStore?.settings['CUSTOMER_AUTHENTICATION_SELECTION'] ===
        'MANDATORY' ||
        this.selectedStore?.settings['CUSTOMER_AUTHENTICATION_SELECTION'] ===
          'OPTIONAL') &&
      !this.locationService.isAdminOrderCaptureUpdate()
    );
  }

  customerAuthRequired(): boolean {
    return (
      this.selectedStore?.settings['CUSTOMER_AUTHENTICATION_SELECTION'] ===
        'MANDATORY' && !this.locationService.isAdminOrderCaptureUpdate()
    );
  }

  membershipRequired(): boolean {
    return (
      this.enableCustomerAuth() &&
      this.selectedStore?.settings['MEMBER_ONLY_AUTHENTICATION']
    );
  }

  isUserLoggedIn(): boolean {
    return (
      this.sociallyLoggedIn && (!this.membershipRequired() || this.isMember)
    );
  }

  checkCurrentSession() {
    try {
      const data: CustomerSocialLoginResponse = JSON.parse(
        this.localStorageService.getSavedState(this.tokenCookieName) || null,
      );
      if (data && !this.sociallyLoggedIn) {
        const event = {
          src: 'CustomerAuthService',
          description: 'Current session loaded',
        };

        this.sociallyLoggedIn = true;

        this.storeService.refreshToken(data.tokens.refreshToken).subscribe({
          next: (res) => {
            this.isMember = true;
            data.tokens = res;
            this.saveState(data);
            this.store.dispatch(new SocialLoginSuccess(data, event));
            this.store.dispatch(
              new AddOrderMeta('customerEmail', data.email, event),
            );
            this.startRefreshTokenInterval(data);
          },
          error: () => this.logout(),
        });
      }
    } catch (error) {
      this.localStorageService.removeSavedState(this.tokenCookieName);
      console.error('An error occurred:', error);
    }
  }

  login(data: any) {
    if (data?.mode === 'customer-login') {
      this.sociallyLoggedIn = true;
      this.saveState(data);
      const socialAccountDetails: SocialAccountLoginDetails = {
        provider: (data.provider as string).toLowerCase(),
        accessToken: data.authToken || data.accessToken,
        countryId: this.selectedStore.address.country.code,
        appleCode: data.appleCode,
        firstName: data.firstName,
        lastName: data.lastName,
      };

      const event = {
        src: 'CustomerAuthService',
        description: 'User Social Authentication',
      };
      this.store.dispatch(new AddOrderMeta('customerEmail', data.email, event));
      this.store.dispatch(new SocialLoginLoading(event));

      this.storeService.socialLogin(socialAccountDetails).subscribe({
        next: (res) => {
          this.saveState(res);
          this.store.dispatch(new SocialLoginSuccess(res, event));
          this.startRefreshTokenInterval(res);
        },
        error: (error) => {
          this.store.dispatch(new SocialLoginFailed());
        },
      });
    }
  }

  doRefreshToken(data: CustomerSocialLoginResponse) {
    const event = {
      src: 'CustomerAuthService',
      description: 'Refreshing user token',
    };

    this.storeService.refreshToken(data.tokens.refreshToken).subscribe({
      next: (res) => {
        data.tokens = res;
        this.saveState(data);
        this.store.dispatch(new SocialLoginSuccess(data, event));
      },
      error: () => this.logout(),
    });
  }

  private startRefreshTokenInterval(data: CustomerSocialLoginResponse) {
    this.clearRefreshTokenInterval();
    this.refreshTokenInterval = setInterval(() => {
      this.doRefreshToken(data);
    }, this.REFRESH_TOKEN_INTERVAL);
  }

  private clearRefreshTokenInterval() {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = null;
    }
  }

  saveState(data: any) {
    this.localStorageService.setSavedState(
      JSON.stringify(data),
      this.tokenCookieName,
    );
  }

  passwordLogin(
    { email, password }: { email: string; password: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    const socialAccountDetails: SocialAccountLoginDetails = {
      username: email,
      password: password,
    };

    const event = {
      src: 'CustomerAuthService',
      description: 'User authenticated with password',
    };

    this.storeService.socialLogin(socialAccountDetails).subscribe(
      (response) => {
        this.sociallyLoggedIn = true;
        this.localStorageService.setSavedState(
          JSON.stringify(response),
          this.tokenCookieName,
        );
        this.store.dispatch(new SocialLoginSuccess(response, event));
        this.store.dispatch(new AddOrderMeta('customerEmail', email, event));
        onSuccess && onSuccess(response);
      },
      (error) => {
        this.sociallyLoggedIn = false;
        onError && onError(error);
      },
    );
  }

  customerRegistrationV2(
    email: string,
    captchaToken: string,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService.customerRegistrationV2(email, captchaToken).subscribe(
      (res) => onSuccess && onSuccess(res),
      (error) => {
        onError && onError(error);
      },
    );
  }

  customerResetByCode(
    { email }: { email: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService.customerResetByCode(email).subscribe(
      (res) => onSuccess && onSuccess(res),
      (error) => onError && onError(error),
    );
  }

  customerVerification(
    { email, code }: { email: string; code: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService.customerActivation(email, code).subscribe(
      (res) => {
        this.sociallyLoggedIn = true;
        this.localStorageService.setSavedState(
          JSON.stringify(res),
          this.tokenCookieName,
        );
        const event = {
          src: 'CustomerAuthService',
          description: 'User registered and verified',
        };
        this.store.dispatch(new SocialLoginSuccess(res, event));
        this.store.dispatch(new AddOrderMeta('customerEmail', email, event));
        onSuccess && onSuccess(res);
      },
      (error) => onError && onError(error),
    );
  }

  changePassword(
    { code, password }: { code: string; password: string },
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (response: any) => void;
      onError?: (error: any) => void;
    } = {},
  ) {
    this.storeService.customerPasswordUpdateByCode(code, password).subscribe(
      (res) => onSuccess && onSuccess(res),
      (error) => onError && onError(error),
    );
  }

  logout() {
    this.sociallyLoggedIn = false;
    this.isMember = false;
    this.clearRefreshTokenInterval();
    this.localStorageService.removeSavedState(this.tokenCookieName);
    const event = {
      src: 'CustomerAuthService',
      description: 'User logged out',
    };
    this.store.dispatch(new AddOrderMeta('customerUserId', null, event));
    this.store.dispatch(new SocialLogout(event));
    const retries = 3;
    this.postMessageToIframe(retries);
  }

  private postMessageToIframe(retries = 0) {
    const iframe = window.frames['user-login']?.contentWindow;
    const userLogin = this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.adminHostURL + '/user-login',
    );
    if (iframe) {
      iframe.postMessage(
        { logout: true },
        this.sanitizer.sanitize(SecurityContext.URL, userLogin),
      );
    } else if (retries > 0) {
      setTimeout(() => this.postMessageToIframe(retries - 1), 1000);
    }
  }

  private onInvalidMember() {
    this.toasterService.error(
      this.translateService.instant('public.checkout.memberOnlyError'),
    );
    this.isMember = false;
    this.logout();
  }

  getAuthQueryParams(): string {
    if (!this.isUserLoggedIn()) return '';
    const authData = JSON.parse(
      this.localStorageService.getSavedState(this.tokenCookieName) || '{}',
    );
    const jsonString = JSON.stringify(authData);
    const base64 = btoa(jsonString);
    return `&${this.authDataParam}=${encodeURIComponent(base64)}`;
  }

  parseAndSetAuthFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const authDataParam = urlParams.get(this.authDataParam);
    if (authDataParam) {
      const decoded = decodeURIComponent(authDataParam);
      const jsonString = atob(decoded);
      const authData = JSON.parse(jsonString);
      this.saveState(authData);
    }
  }
}
